import * as THREE from 'three';
import gsap from 'gsap';
import { getDAMProxyLink } from './utils';

type LayeredCanvasOptions = {
  container: HTMLElement;
  videoFallbackEl?: HTMLVideoElement;
  onDataLoaded?: () => void;
  isVideo?: boolean;
  assetUrl?: string;
  poster?: string;
  reduceMotion?: boolean;
  shouldBeSubtle?: boolean;
};

export default class LayeredCanvas {
  private scene: THREE.Scene;
  private camera: THREE.OrthographicCamera;
  private renderer: THREE.WebGLRenderer;
  private layers: THREE.Mesh[] = [];
  private texture: THREE.Texture = null;
  private video: HTMLVideoElement = null;
  private container: HTMLElement;
  private videoFallbackEl?: HTMLVideoElement;
  private onDataLoaded: () => void;
  private width: number;
  private height: number;
  private scaleX: number = 1;
  private scaleY: number = 1;
  private reduceMotion: boolean = false;
  private shouldBeSubtle: boolean = false;
  private resizeObserver: ResizeObserver | null = null;

  private masterTl: gsap.core.Timeline | null = null;

  constructor(options: LayeredCanvasOptions) {
    this.container = options.container;
    this.onDataLoaded = options.onDataLoaded || (() => {});
    this.reduceMotion = options.reduceMotion || false;
    this.shouldBeSubtle = options.shouldBeSubtle || false;
    this.videoFallbackEl = options.videoFallbackEl;

    // Container starts scaled down + faded out
    gsap.set(this.container, {
      opacity: 0,
      scale: (!this.shouldBeSubtle) ? 0.8 : 1
    });

    this.width = this.container.clientWidth;
    this.height = this.container.clientHeight;

    this.scene = new THREE.Scene();
    this.camera = new THREE.OrthographicCamera(
      -this.width / 2,
      this.width / 2,
      this.height / 2,
      -this.height / 2,
      0.1,
      10
    );
    this.camera.position.z = 1;

    this.renderer = new THREE.WebGLRenderer({ antialias: true });
      this.renderer.setPixelRatio(window.devicePixelRatio || 1);
    this.renderer.setSize(this.width, this.height);
    this.container.appendChild(this.renderer.domElement);

    this.loadAssets({ asset: options.assetUrl, poster: options.poster }, options.isVideo).then(async () => {
      this.createLayers();
      await this.onResize();
      this.setLayersStart();
      this.render();
      this.animateLayers();
    });

    this.resizeObserver = new ResizeObserver(() => {
      this.onResize();
    });
    this.resizeObserver.observe(this.container);
  }

  // --------------------

  private loadAssets = async ({ asset, poster }: { asset: string; poster?: string; }, isVideo = false) => {
    //console.log(`Loading asset: ${asset}, isVideo: ${isVideo}`);
    if (isVideo) {
      await this.loadVideoTexture(asset, poster);
    } else {
      await this.loadImageTexture(asset);
    }
  };

  private loadImageTexture = (url: string) => {
    return new Promise<void>((resolve) => {
      const loader = new THREE.TextureLoader();
      // Ensure cross-origin is allowed so loading remote images doesn't taint
      // the WebGL canvas. Prefer setCrossOrigin when available, otherwise
      // fall back to setting the property directly.
      try {
        // @ts-ignore - some builds expose setCrossOrigin
        if (typeof loader.setCrossOrigin === 'function') loader.setCrossOrigin('anonymous');
        // @ts-ignore
        loader.crossOrigin = 'anonymous';
      } catch (e) {
        // noop
      }

      const assetUrl = getDAMProxyLink(url);
      loader.load(assetUrl, (texture) => {
        texture.minFilter = THREE.LinearFilter;
        this.texture = texture;
        this.video = null;
        // Notify consumer that image data is ready
        try {
          this.onDataLoaded();
        } catch (e) {
          // swallow errors from callback to avoid breaking the loader
          // but keep the image loading flow intact
          // console.warn('onDataLoaded threw', e);
        }
        resolve();
      });
    });
  };

  private loadVideoTexture = (url: string, poster: string) => {
    return new Promise<void>((resolve) => {
      const video = document.createElement('video');
        this.video = video;
        
      video.crossOrigin = 'anonymous';
      video.loop = true;
      video.muted = true;
      video.poster = poster || '';
      video.playsInline = true;
        video.addEventListener("loadedmetadata", () => {
          if (this.video && this.video.videoWidth) {
            this.onResize();
          }
        });
          video.style.display = "none";
          this.container.appendChild(video);
        video.addEventListener("resize", () => {
          if (this.video && this.video.videoWidth) {
            this.onResize();
          }
        });
      video.addEventListener('loadeddata', () => {
        const texture = new THREE.VideoTexture(video);
        texture.minFilter = THREE.LinearFilter;
        texture.magFilter = THREE.LinearFilter;
        texture.format = THREE.RGBAFormat;
        texture.colorSpace = THREE.SRGBColorSpace;
        this.texture = texture;
        
        if (!this.reduceMotion) {
          video.play();
        }
        this.onDataLoaded();
        resolve();
      });
      video.addEventListener('error', () => {
        video.remove();
        const basicVideo = document.createElement('video');
        basicVideo.classList.add('video-fallback');
        basicVideo.loop = true;
        basicVideo.muted = true;
        basicVideo.playsInline = true;
        basicVideo.poster = poster || '';
        this.video = basicVideo;
        basicVideo.src = url;
        basicVideo.load();
          basicVideo.addEventListener("loadedmetadata", () => {
            if (this.video && this.video.videoWidth) {
              this.onResize();
            }
          });
        basicVideo.addEventListener('loadeddata', () => {
          if (!this.reduceMotion) {
            basicVideo.play();
          }
          this.onDataLoaded();
        });
        this.container.appendChild(basicVideo);
        resolve();
      });
      video.src = getDAMProxyLink(url);
      video.load();
    });
  };

  // --------------------

  // Create multiple layers with slight depth offsets
  private createLayers() {
    if (!this.texture) {
      //console.error('No texture loaded. Cannot create layers.');
      return;
    }
    const depthStep = 0.01;
    const geometry = new THREE.PlaneGeometry(1, 1);
    const material = new THREE.MeshBasicMaterial({
      map: this.texture,
      transparent: true,
    });
    for (let i = 0; i < 5; i++) {
      const mesh = new THREE.Mesh(geometry, material);
      mesh.position.z = i * depthStep;
      this.scene.add(mesh);
      this.layers.push(mesh);
    }
  };

  // Set initial layer positions and scales
  private setLayersStart() {
    for (let i = 0; i < this.layers.length; i++) {
      const mesh = this.layers[i];
      const scaleFactorBase = (!this.shouldBeSubtle) ? 0.75 : 0.9;
      const scaleFactor = Math.pow(scaleFactorBase, i);
      gsap.set(mesh.scale, {
        x: this.scaleX * scaleFactor,
        y: this.scaleY * scaleFactor,
      });
    }
  };

  // Create animation timelines
  public animateLayers() {

    // Seek the internal video back to frame 0 so each entry plays from the beginning.
    if (this.video) { try { this.video.currentTime = 0; } catch {} }

    // Create master timeline
    this.masterTl = gsap.timeline().add('start');

    // Fade in the container
    this.masterTl.to(this.container, {
      opacity: 1,
      scale: 1,
      duration: 1,
      ease: 'power4.inOut',
    }, 'start');

    // Add new label for layers animation
    if (!this.shouldBeSubtle) {
      this.masterTl.add('layers-in');
    } else {
      this.masterTl.add('layers-in', 'start+=0.5');
    }

    // Loop through layers and animate
    for (let i = 0; i < this.layers.length; i++) {
      const mesh = this.layers[i];

      // Create timeline for this layer
      const layerTl = gsap.timeline();
      layerTl.to(mesh.scale, {
        x: this.scaleX,
        y: this.scaleY,
        duration: 1,
        ease: 'power4.inOut',
      });

      // Add to master timeline
      this.masterTl.add(layerTl, 'layers-in');
    }
  };

  // Render loop
  private render = () => {
    // Update video textures if any
    if (this.texture instanceof THREE.VideoTexture) {
      this.texture.needsUpdate = true;
    }
    this.renderer.render(this.scene, this.camera);
    requestAnimationFrame(this.render);
  };

  // Scale layers based on container and source dimensions
  private scaleLayers() {
    this.layers.forEach((mesh) => {
        if (gsap.isTweening(mesh.scale)) {
          gsap.killTweensOf(mesh.scale);
          gsap.to(mesh.scale, {
            x: this.scaleX,
            y: this.scaleY,
            z: 1,
            duration: 0.4,
            ease: "power2.out"
          });
        } else {
          mesh.scale.set(this.scaleX, this.scaleY, 1);
        }
      });
    };

    // Handle window resize
    private onResize = () => {
    this.width = this.container.clientWidth;
    this.height = this.container.clientHeight;
    this.camera.left = -this.width / 2;
    this.camera.right = this.width / 2;
    this.camera.top = this.height / 2;
    this.camera.bottom = -this.height / 2;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(this.width, this.height);


    // Get source dimensions
      let sourceWidth = 16, sourceHeight = 9;
      if (this.video && this.video.videoWidth) {
        sourceWidth = this.video.videoWidth;
        sourceHeight = this.video.videoHeight;
      } else if (this.texture && this.texture.image) {
        if (this.texture.image.videoWidth) {
          sourceWidth = this.texture.image.videoWidth;
          sourceHeight = this.texture.image.videoHeight;
        } else if (this.texture.image.naturalWidth) {
          sourceWidth = this.texture.image.naturalWidth;
          sourceHeight = this.texture.image.naturalHeight;
        } else if (this.texture.image.width) {
          sourceWidth = this.texture.image.width;
          sourceHeight = this.texture.image.height;
        }
      }

    // Calculate scale for object-fit: cover
    const containerRatio = this.width / this.height;
    const sourceRatio = sourceWidth / sourceHeight;
      let targetWidth = this.width;
      let targetHeight = this.height;
      if (sourceRatio > containerRatio) {
        targetWidth = this.height * sourceRatio;
        targetHeight = this.height;
      } else {
        targetWidth = this.width;
        targetHeight = this.width / sourceRatio;
      }
      this.scaleX = targetWidth;
      this.scaleY = targetHeight;
    this.scaleLayers();
  };

  dispose() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
    this.renderer.dispose();
    this.texture?.dispose();
    this.layers.forEach((mesh) => mesh.geometry.dispose());
    this.layers.forEach((mesh) => (mesh.material as THREE.Material).dispose());
    this.masterTl?.kill();
    this.video.pause();
    this.container.removeChild(this.renderer.domElement);
  }
}
